import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Box, CircularProgress } from '@mui/material';
import { AxiosError } from 'axios';

import { isAuthEnabled } from '@/auth';
import { paths } from '@/routes/paths';
import { useMyPermissions } from '@/queries/usePermissions';
import { useAuthStore } from '@/stores/useAuthStore';

export function ProtectedRoute() {
  const { accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();
  const { setPermissions, permissions: storePermissions } = useAuthStore();
  const {
    data: permissions,
    isSuccess,
    isError,
    error,
  } = useMyPermissions(isAuthenticated);

  useEffect(() => {
    if (isSuccess && permissions) {
      setPermissions(permissions);
    }
  }, [isSuccess, permissions, setPermissions]);

  // Render the route once the Zustand store has been populated and the latest query
  // is not in an error state. Downstream pages (e.g. SkillsMatrixPage, Sidebar) read
  // `permissions` directly from the store — waiting for the useEffect sync prevents
  // a one-frame null-permissions window that would otherwise redirect them to dashboard.
  // Background refetches flow through without unmounting. A 401/403 propagates via the
  // Axios interceptor, which clears the store and redirects, so blocking on isError is safe.
  const hasVerifiedPermissions = storePermissions !== null && !isError;

  if (!isAuthEnabled()) {
    return <Outlet />;
  }

  if (inProgress !== InteractionStatus.None) {
    return null;
  }

  if (accounts.length === 0) {
    // Save original URL to sessionStorage (survives MSAL redirect)
    sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search);
    return <Navigate to={paths.login} replace />;
  }

  // Handle 403 - user authenticated but not registered
  if (isError && error) {
    const axiosError = error as AxiosError;
    if (axiosError.response?.status === 403) {
      return <Navigate to={paths.notRegistered} replace />;
    }
  }

  // Show loading until we've verified permissions with a fresh API call
  // hasVerifiedPermissions ensures we don't render UI based on stale cache
  if (!hasVerifiedPermissions) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return <Outlet />;
}

export default ProtectedRoute;
